import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { institutionService, InstitutionRow } from '../../lib/institutionService';
import { supabase } from '../../lib/supabase';
import ConsultationRequestModal from './components/ConsultationRequestModal';
import DetailTabs from './components/DetailTabs';
import { useSeoSchema } from '@/hooks/useSeoSchema';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://healthtravelturkiye.com';

interface DoctorRow {
  id: string;
  name: string;
  title: string | null;
  specialization: string | null;
  specialty_category: string | null;
  experience_years: number | null;
  photo_url: string | null;
  bio: string | null;
  cv_file_url: string | null;
}

export default function InstitutionDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [institution, setInstitution] = useState<InstitutionRow | null>(null);
  const [doctors, setDoctors] = useState<DoctorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showConsultationModal, setShowConsultationModal] = useState(false);
  const [showProOnlyModal, setShowProOnlyModal] = useState(false);

  useEffect(() => {
    if (id) loadInstitution(id);
  }, [id]);

  // Dynamic schema + BreadcrumbList — re-runs when institution loads
  useSeoSchema(
    'schema-institution-detail',
    institution
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'MedicalClinic',
            name: institution.name,
            description: institution.description
              ? institution.description.slice(0, 155)
              : `${institution.name} - Health institution in ${institution.city} offering health tourism services.`,
            url: `${SITE_URL}/kurum/${institution.id}`,
            address: {
              '@type': 'PostalAddress',
              addressLocality: institution.city || '',
              addressRegion: institution.district || '',
              addressCountry: 'TR',
              streetAddress: institution.address || '',
            },
            telephone: institution.phone || undefined,
            email: institution.email || undefined,
          },
        ]
      : [],
    institution
      ? [
          { nameKey: 'breadcrumb_institutions', url: `${SITE_URL}/saglik-kurumlari` },
          { name: institution.name, url: `${SITE_URL}/kurum/${institution.id}` },
        ]
      : [],
    i18n.language,
  );

  useEffect(() => {
    if (!institution) return;
    document.title = `${institution.name} - Health Travel Türkiye`;
    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    const desc = institution.description
      ? institution.description.slice(0, 155)
      : `${institution.name} - Health institution in ${institution.city} offering health tourism services.`;
    setMeta('description', desc);
    setMeta('keywords', `${institution.name}, health tourism, ${institution.city}, Turkey`);
    setMeta('og:title', `${institution.name} - Health Travel Türkiye`, true);
    setMeta('og:description', desc, true);
    setMeta('og:url', `${SITE_URL}/kurum/${institution.id}`, true);
    const ogImage = institution.cover_image_url || 'https://public.readdy.ai/ai/img_res/2affc022cf6ca26fd11775079b665f3a.jpg';
    setMeta('og:image', ogImage, true);
    setMeta('og:image:width', '1200', true);
    setMeta('og:image:height', '630', true);
    setMeta('og:image:alt', `${institution.name} - ${institution.city || 'Türkiye'}`, true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', `${institution.name} - Health Travel Türkiye`);
    setMeta('twitter:description', desc);
    setMeta('twitter:image', ogImage);
    setMeta('twitter:image:alt', `${institution.name} - ${institution.city || 'Türkiye'}`);
  }, [institution]);

  const loadInstitution = async (instId: string) => {
    setLoading(true);
    try {
      const { data, error } = await institutionService.getById(instId);
      if (error || !data) { setNotFound(true); setLoading(false); return; }
      setInstitution(data);
      const { data: docs } = await supabase
        .from('doctors').select('*').eq('institution_id', instId).order('created_at', { ascending: true });
      setDoctors((docs || []) as DoctorRow[]);
    } catch {
      setNotFound(true);
    }
    setLoading(false);
  };

  const handleConsultationClick = () => {
    if (!institution?.is_pro_member) { setShowProOnlyModal(true); return; }
    setShowConsultationModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 font-['Outfit'] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-10 h-10 border-[3px] border-rose-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-sm text-gray-500">{t('detail_loading')}</p>
        </div>
      </div>
    );
  }

  if (notFound || !institution) {
    return (
      <div className="min-h-screen bg-gray-50 font-['Outfit'] flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center" style={{ paddingTop: 'var(--navbar-height)' }}>
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
              <i className="ri-hospital-line text-4xl text-gray-400"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('detail_not_found_title')}</h2>
            <p className="text-gray-500 mb-6">{t('detail_not_found_desc')}</p>
            <Link to="/saglik-kurumlari" className="px-6 py-3 bg-rose-500 text-white rounded-xl font-semibold hover:bg-rose-600 transition-colors text-sm whitespace-nowrap cursor-pointer">
              {t('detail_not_found_btn')}
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const services = institution.services || [];
  const workingHoursData = institution.working_hours as Record<string, { open: string; close: string; closed: boolean }> | null;

  const getWorkingHoursText = () => {
    if (!workingHoursData) return null;
    const openDays = Object.entries(workingHoursData)
      .filter(([, v]) => !v.closed)
      .map(([k, v]) => `${k}: ${v.open}-${v.close}`);
    return openDays.length > 0 ? openDays[0] : null;
  };

  const institutionForTabs = {
    id: institution.id,
    name: institution.name,
    description: institution.description || '',
    services,
    departments: institution.departments || [],
    doctors: doctors.map(d => ({
      name: d.name,
      title: d.title || '',
      branch: (d as any).branch || d.title || '',
      specialty: d.specialization || '',
      specialtyCategory: d.specialty_category || '',
      experience: d.experience_years || 0,
      image: d.photo_url || '',
      bio: d.bio || '',
      cvFileUrl: d.cv_file_url || '',
    })),
    certificates: institution.certificates || [],
    tech_devices: institution.tech_devices || [],
    workingHours: getWorkingHoursText() || '',
    address: institution.address || '',
    city: institution.city || '',
    district: institution.district || '',
    email: institution.email || '',
    website: institution.website || '',
    foundedYear: institution.foundation_year || undefined,
    coverImage: institution.cover_image_url || '',
    workingHoursData: workingHoursData || undefined,
  };

  return (
    <div className="min-h-screen bg-gray-50 font-['Outfit']">
      <Navbar />

      <div style={{ paddingTop: 'var(--navbar-height, 160px)' }}>
        {/* Hero */}
        <div className="relative h-96 md:h-[520px] overflow-hidden bg-gray-950">
          {institution.cover_image_url ? (
            <img src={institution.cover_image_url} alt={institution.name} className="w-full h-full object-contain" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-rose-400 to-rose-600 flex items-center justify-center">
              <i className="ri-hospital-line text-8xl text-white/30"></i>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center space-x-3 mb-3">
                {institution.city && (
                  <span className="text-white/90 text-sm flex items-center">
                    <i className="ri-map-pin-2-fill mr-1 text-rose-300"></i>
                    {institution.city}{institution.district ? `, ${institution.district}` : ''}
                  </span>
                )}
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">{institution.name}</h1>
              <div className="flex flex-wrap items-center gap-4">
                {institution.foundation_year && (
                  <span className="text-white/70 text-sm flex items-center">
                    <i className="ri-calendar-line mr-1"></i>
                    {t('detail_serving_since', { year: institution.foundation_year })}
                  </span>
                )}
                {doctors.length > 0 && (
                  <span className="text-white/70 text-sm flex items-center">
                    <i className="ri-user-heart-line mr-1"></i>
                    {t('detail_expert_doctors', { count: doctors.length })}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <DetailTabs institution={institutionForTabs} />
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Consultation Card */}
                <div className="bg-white rounded-2xl shadow-md p-6">
                  <button
                    onClick={handleConsultationClick}
                    className="w-full px-6 py-3.5 bg-gradient-to-r from-rose-500 to-rose-400 text-white rounded-xl font-semibold hover:from-rose-600 hover:to-rose-500 transition-all text-sm whitespace-nowrap cursor-pointer shadow-lg shadow-rose-200/50"
                  >
                    <i className="ri-chat-heart-line mr-2"></i>
                    {t('detail_consultation_btn')}
                  </button>
                </div>

                {/* Quick Info Card */}
                {(institution.foundation_year || doctors.length > 0) && (
                  <div className="bg-white rounded-2xl shadow-md p-6">
                    <h3 className="text-sm font-bold text-gray-900 mb-4">{t('detail_institution_info')}</h3>
                    <div className="space-y-3">
                      {institution.foundation_year && (
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">{t('detail_experience')}</span>
                          <span className="text-sm font-bold text-amber-600">
                            {t('detail_experience_years', { count: new Date().getFullYear() - institution.foundation_year })}
                          </span>
                        </div>
                      )}
                      {doctors.length > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">{t('detail_expert_staff')}</span>
                          <span className="text-sm font-bold text-gray-700">
                            {t('detail_doctor_count', { count: doctors.length })}
                          </span>
                        </div>
                      )}
                      {services.length > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">{t('detail_service_count')}</span>
                          <span className="text-sm font-bold text-teal-600">
                            {t('detail_service_count_val', { count: services.length })}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Accreditation Logos */}
                <div className="bg-white rounded-2xl shadow-md p-5">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
                    {t('detail_official_accreditation')}
                  </h3>
                  <div className="space-y-3">
                    {[
                      {
                        src: 'https://storage.readdy-site.link/project_files/b825b40c-ced1-45bc-acd7-e75b219f4e94/d3120357-b604-44a0-aefb-b4527d8be74a_Health-Turkiye-Logo.svg?v=733eda141137079f8cc11e4f506bec91',
                        alt: 'Health Türkiye',
                        titleKey: 'detail_health_turkey',
                        descKey: 'detail_health_turkey_desc',
                      },
                      {
                        src: 'https://storage.readdy-site.link/project_files/b825b40c-ced1-45bc-acd7-e75b219f4e94/f60d29a1-c69a-4180-b9bf-f3bf3f7fe15d_Republic-of-Turkiye-Ministry-of-Health-Logo.png?v=71e00ccd8f6d40e8d4801bf4a26e4341',
                        alt: 'Ministry of Health',
                        titleKey: 'detail_ministry_health',
                        descKey: 'detail_ministry_health_desc',
                      },
                      {
                        src: 'https://storage.readdy-site.link/project_files/b825b40c-ced1-45bc-acd7-e75b219f4e94/d8a5e1d4-49c4-4acf-997a-18d6539223bc_Republic-of-Turkiye-Ministry-of-Cultureand-Tourism-Logo.png?v=1f9b9ce043aa351352a5d6d7c5120a9b',
                        alt: 'Ministry of Culture and Tourism',
                        titleKey: 'detail_ministry_culture',
                        descKey: 'detail_ministry_culture_desc',
                      },
                    ].map((item) => (
                      <div key={item.titleKey} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                        <div className="w-20 h-20 flex items-center justify-center flex-shrink-0">
                          <img src={item.src} alt={item.alt} className="w-full h-full object-contain" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-gray-800 leading-tight">{t(item.titleKey)}</p>
                          <p className="text-[10px] text-gray-400 mt-0.5">{t(item.descKey)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Consultation Modal */}
      {showConsultationModal && (
        <ConsultationRequestModal
          institutionId={institution.id}
          institutionName={institution.name}
          services={services}
          onClose={() => setShowConsultationModal(false)}
        />
      )}

      {/* Pro Only Modal */}
      {showProOnlyModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">
            <div className="relative bg-gradient-to-br from-amber-500 to-orange-400 p-8 text-center">
              <button
                onClick={() => setShowProOnlyModal(false)}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-4">
                <i className="ri-vip-crown-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-1">{t('pro_only_title')}</h3>
              <p className="text-sm text-white/85">{t('pro_only_desc')}</p>
            </div>
            <div className="p-6">
              <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl mb-5">
                <p className="text-sm text-amber-800 flex items-start gap-2">
                  <i className="ri-information-line text-amber-500 mt-0.5 flex-shrink-0 text-base"></i>
                  <span dangerouslySetInnerHTML={{ __html: t('pro_only_info') }} />
                </p>
              </div>
              <div className="space-y-3">
                <Link
                  to="/saglik-kurumlari"
                  onClick={() => setShowProOnlyModal(false)}
                  className="w-full px-6 py-3.5 bg-gradient-to-r from-rose-500 to-rose-400 text-white rounded-xl font-semibold hover:from-rose-600 hover:to-rose-500 transition-all text-sm whitespace-nowrap cursor-pointer shadow-lg shadow-rose-200/50 flex items-center justify-center"
                >
                  <i className="ri-search-line mr-2"></i>
                  {t('pro_only_explore')}
                </Link>
                <button
                  onClick={() => setShowProOnlyModal(false)}
                  className="w-full px-6 py-3.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all text-sm whitespace-nowrap cursor-pointer"
                >
                  {t('pro_only_close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
